import createClient from "openapi-fetch";
import type { paths } from "./schema";
import { ApiError, toApiError } from "./errors";

/**
 * The single fetch site in the bundle (§B1.1). Nothing else calls `fetch`, and eslint enforces it.
 *
 * Auth: the session cookie is `HttpOnly` and travels automatically with `credentials: "include"`. The CSRF token
 * is a **synchronizer token** (§A2.2, v3-M-4): it is returned in the body of `POST`/`GET /api/v1/session`, held in
 * memory by `SessionProvider`, and sent as `X-CSRF-Token` on every mutating call. The bundle never reads
 * `document.cookie` — the cookie's name differs between dev and production while the bundle is the same file.
 */

type CsrfSink = {
  token: string | null;
  onUnauthorized: () => void;
  /** xmg-sec-mfa: a `403 mfa_required` / `mfa_enrollment_required` from ANY call - the session is signed in with
   * a password only. The provider records it and `RequireSession` sends the user to the right MFA screen. */
  onMfaRequired: (state: "required" | "enrollment_required") => void;
};

/** Module-level, not React state: the client is created once and the provider syncs into it. */
export const sessionSink: CsrfSink = { token: null, onUnauthorized: () => {}, onMfaRequired: () => {} };

/**
 * The app is always same-origin: in dev through §C1.1's Vite proxy, in production behind one host. `baseUrl` is
 * therefore the current origin rather than the string `"/"`. openapi-fetch builds a `new Request(url)` before it
 * calls `fetch`, and `new Request("/api/v1/…")` throws `Failed to parse URL` under Node — which is what vitest's
 * jsdom environment runs. Reading `location.origin` keeps the request same-origin in the browser *and* makes the
 * whole test suite work without a second fetch implementation or a per-test base URL.
 */
const ORIGIN = typeof window === "undefined" ? "http://localhost" : window.location.origin;

export const client = createClient<paths>({
  baseUrl: ORIGIN,
  credentials: "include",
  headers: { Accept: "application/json" },
  /**
   * Resolve `fetch` **at call time**, not at module-evaluation time. openapi-fetch otherwise captures
   * `globalThis.fetch` when `createClient` runs, which is when this module is first imported — before
   * `tests/setup.ts`'s `beforeAll` has installed msw's interceptor. The client would then hold the unpatched
   * reference and every screen test would make a real network call. One indirection, and the behaviour in the
   * browser is unchanged.
   */
  fetch: (request) => globalThis.fetch(request),
});

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

client.use({
  onRequest({ request }) {
    if (!SAFE_METHODS.has(request.method.toUpperCase()) && sessionSink.token !== null) {
      request.headers.set("X-CSRF-Token", sessionSink.token);
    }
    return request;
  },
});

/** §B1.1/§A9.6: one key per user gesture, reused across every retry of that gesture. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** Headers for a mutating call that carries an idempotency key. */
export function idempotent(key: string): Record<string, string> {
  return { "Idempotency-Key": key };
}

type FetchResult<T> = { data?: T; error?: unknown; response: Response };

/**
 * Turn openapi-fetch's `{data, error, response}` into a value or a thrown `ApiError`, which is what TanStack Query
 * wants. A 401 additionally notifies the session provider, which is what sends `RequireSession` to `/login`.
 */
export async function unwrap<T>(call: Promise<FetchResult<T>>): Promise<T> {
  const { data, error, response } = await call;
  if (response.ok && error === undefined) {
    return data as T;
  }
  const requestId = response.headers.get("X-Request-Id");
  const apiError = toApiError(response.status, error, requestId);
  if (apiError.status === 401) {
    sessionSink.onUnauthorized();
  } else if (apiError.status === 403 && apiError.is("mfa_required")) {
    sessionSink.onMfaRequired("required");
  } else if (apiError.status === 403 && apiError.is("mfa_enrollment_required")) {
    sessionSink.onMfaRequired("enrollment_required");
  }
  throw apiError;
}

/** A download link target (§B6.3): a plain same-origin path, never a fetch. */
export function downloadUrl(migrationId: string, packId: string): string {
  return `/api/v1/migrations/${migrationId}/evidence/${packId}/download`;
}

export { ApiError };
